// Football's weekly challenge (SCENARIOS Q9, PLAN-2026-11-01-the-weekly-challenge):
// the positions a week can draw and the categories each can be scored on.
//
// FAMILIES KEEP A WEEK FROM BEING ONE QUESTION. Passing yards, completions
// and passing first downs rise and fall together, so a week that drew all
// three would ask only "who is the best passer"; a week draws one category
// of a family at most, so its three ask three different things.
//
// THE POINTS ARE ROUND AND FAMILIAR, one value a stat wherever it is scored:
// what the site's default scoring gives it (a touchdown 6, a touchdown pass
// 4, a catch 1, ten yards 1, twenty-five passing yards 1, an interception
// minus 2), and for a stat fantasy does not score, a whole number on the
// same scale (a carry, a target, a completion or a first down 1, a sack
// minus 1, five yards after the catch 1). The categories do not all have an
// equal say, and a person weighs that as they pick (DIRECTION A6).
// `bun scripts/challenge/history.ts --calibrate` prints the say each has.
//
// No fumbles: so rare that one would decide a week. Interceptions and sacks
// are frequent enough, and are drawn less often than the rest.

import type { ChallengeCategoryDef, WeeklyChallengeDef } from '@fsp/sports-core';

const c = (def: ChallengeCategoryDef): ChallengeCategoryDef => def;

/** The receiving categories, the same for every position that catches. */
const receiving = (): ChallengeCategoryDef[] => [
  c({
    id: 'rec',
    name: 'Catches',
    short: 'Rec',
    unit: 'catches',
    of: ['rec'],
    points: 1,
    family: 'catching',
  }),
  c({
    id: 'rec_tgt',
    name: 'Targets',
    short: 'Tgt',
    unit: 'targets',
    of: ['rec_tgt'],
    points: 1,
    family: 'catching',
  }),
  c({
    id: 'rec_yd',
    name: 'Receiving yards',
    short: 'Rec yds',
    unit: 'receiving yards',
    of: ['rec_yd'],
    points: 1,
    every: 10,
    family: 'yards',
  }),
  c({
    id: 'rec_fd',
    name: 'Receiving first downs',
    short: 'Rec 1D',
    unit: 'first downs',
    of: ['rec_fd'],
    points: 1,
    family: 'yards',
  }),
  c({
    id: 'rec_yac',
    name: 'Yards after the catch',
    short: 'YAC',
    unit: 'yards after the catch',
    of: ['rec_yac'],
    points: 1,
    every: 5,
    family: 'after-catch',
  }),
  c({
    id: 'air_yd',
    name: 'Air yards',
    short: 'Air yds',
    unit: 'air yards',
    of: ['air_yd'],
    points: 1,
    every: 10,
    family: 'downfield',
  }),
  c({
    id: 'rec_td',
    name: 'Receiving touchdowns',
    short: 'TD',
    unit: 'touchdowns',
    of: ['rec_td'],
    points: 6,
    family: 'scoring',
  }),
  c({
    id: 'catch_pct',
    name: 'Catch rate',
    short: 'Catch%',
    unit: 'points of catch rate',
    of: ['rec'],
    rate: { over: ['rec_tgt'], atLeast: 4, percent: true },
    from: 50,
    points: 1,
    every: 5,
    family: 'sure-hands',
    decimals: 1,
  }),
  c({
    id: 'rec_ypr',
    name: 'Yards a catch',
    short: 'Y/R',
    unit: 'yards a catch',
    of: ['rec_yd'],
    rate: { over: ['rec'], atLeast: 2 },
    from: 10,
    points: 1,
    family: 'big-plays',
    decimals: 1,
  }),
];

export const footballChallenge: WeeklyChallengeDef = {
  positionsPerWeek: 2,
  categoriesPerPosition: 3,
  tiers: 3,
  captain: 1.5,
  recentGames: 10,
  fewestGames: 2,
  // A season's first week opens a week before its first game, after the
  // last roster cuts; every later one at the week before's first kickoff;
  // none later than Wednesday morning ahead of its Thursday game. Each is
  // over on Tuesday once its lines are in (DIRECTION A6).
  opens: { weekday: 3, time: '10:00', zone: 'America/New_York' },
  ends: { weekday: 2, time: '10:00', zone: 'America/New_York' },
  positions: [
    {
      position: 'QB',
      tierSize: 8,
      categories: [
        c({
          id: 'pass_yd',
          name: 'Passing yards',
          short: 'Pass yds',
          unit: 'passing yards',
          of: ['pass_yd'],
          points: 1,
          every: 25,
          family: 'passing',
        }),
        c({
          id: 'pass_cmp',
          name: 'Completions',
          short: 'Cmp',
          unit: 'completions',
          of: ['pass_cmp'],
          points: 1,
          family: 'passing',
        }),
        c({
          id: 'pass_fd',
          name: 'Passing first downs',
          short: 'Pass 1D',
          unit: 'first downs',
          of: ['pass_fd'],
          points: 1,
          family: 'passing',
        }),
        c({
          id: 'pass_td',
          name: 'Touchdown passes',
          short: 'Pass TD',
          unit: 'touchdown passes',
          of: ['pass_td'],
          points: 4,
          family: 'scoring',
        }),
        c({
          id: 'cmp_pct',
          name: 'Completion percentage',
          short: 'Cmp%',
          unit: 'points of completion percentage',
          of: ['pass_cmp'],
          rate: { over: ['pass_att'], atLeast: 10, percent: true },
          from: 50,
          points: 1,
          every: 2,
          family: 'accuracy',
          decimals: 1,
        }),
        c({
          id: 'pass_ypa',
          name: 'Yards a pass',
          short: 'Y/A',
          unit: 'yards a pass',
          of: ['pass_yd'],
          rate: { over: ['pass_att'], atLeast: 10 },
          from: 4,
          points: 2,
          family: 'accuracy',
          decimals: 1,
        }),
        c({
          id: 'rush_yd',
          name: 'Rushing yards',
          short: 'Rush yds',
          unit: 'rushing yards',
          of: ['rush_yd'],
          points: 1,
          every: 10,
          family: 'running',
        }),
        c({
          id: 'rush_att',
          name: 'Runs',
          short: 'Runs',
          unit: 'runs',
          of: ['rush_att'],
          points: 1,
          family: 'running',
        }),
        c({
          id: 'pass_int',
          name: 'Interceptions thrown',
          short: 'INT',
          unit: 'interceptions',
          of: ['pass_int'],
          points: 2,
          against: true,
          weight: 0.35,
          family: 'mistakes',
        }),
        c({
          id: 'pass_sack',
          name: 'Sacks taken',
          short: 'Sacks',
          unit: 'sacks',
          of: ['pass_sack'],
          points: 1,
          against: true,
          weight: 0.35,
          family: 'mistakes',
        }),
      ],
    },
    {
      position: 'RB',
      tierSize: 12,
      categories: [
        c({
          id: 'rush_yd',
          name: 'Rushing yards',
          short: 'Rush yds',
          unit: 'rushing yards',
          of: ['rush_yd'],
          points: 1,
          every: 10,
          family: 'running',
        }),
        c({
          id: 'rush_att',
          name: 'Carries',
          short: 'Car',
          unit: 'carries',
          of: ['rush_att'],
          points: 1,
          family: 'running',
        }),
        c({
          id: 'rush_fd',
          name: 'Rushing first downs',
          short: 'Rush 1D',
          unit: 'first downs',
          of: ['rush_fd'],
          points: 1,
          family: 'running',
        }),
        c({
          id: 'rush_ypc',
          name: 'Yards a carry',
          short: 'Y/C',
          unit: 'yards a carry',
          of: ['rush_yd'],
          rate: { over: ['rush_att'], atLeast: 6 },
          from: 2,
          points: 2,
          family: 'burst',
          decimals: 1,
        }),
        c({
          id: 'rec',
          name: 'Catches',
          short: 'Rec',
          unit: 'catches',
          of: ['rec'],
          points: 1,
          family: 'receiving',
        }),
        c({
          id: 'rec_yd',
          name: 'Receiving yards',
          short: 'Rec yds',
          unit: 'receiving yards',
          of: ['rec_yd'],
          points: 1,
          every: 10,
          family: 'receiving',
        }),
        // A back's catches are mostly behind the line, so his yards after
        // the catch are nearly his receiving yards: one family.
        c({
          id: 'rec_yac',
          name: 'Yards after the catch',
          short: 'YAC',
          unit: 'yards after the catch',
          of: ['rec_yac'],
          points: 1,
          every: 5,
          family: 'receiving',
        }),
        c({
          id: 'td',
          name: 'Touchdowns',
          short: 'TD',
          unit: 'touchdowns',
          of: ['rush_td', 'rec_td'],
          points: 6,
          family: 'scoring',
        }),
      ],
    },
    {
      position: 'WR',
      tierSize: 15,
      categories: receiving(),
    },
    {
      position: 'TE',
      tierSize: 8,
      categories: receiving(),
    },
  ],
};
